import React, { useId, useLayoutEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './EmblemAnimated.module.css';
import {
  ORB_FILL,
  ORB_REVEAL_CENTER,
  ORB_REVEAL_MAX_R,
  ORB_STROKE_CIRCLE,
  ORB_VIEW_BOX,
} from './logoPaths';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface EmblemAnimatedProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Width in px; height matches via aspect-ratio. Default: 200. */
  size?: number;
  /** Whether the animation loops indefinitely. Default: true. */
  loop?: boolean;
  /** Draw-on duration in ms (fill + stroke reveal). Default: 1670. */
  duration?: number;
  /** Hold time at completion before restarting (ms). Default: 1000. */
  holdDuration?: number;
  /**
   * Optional fill/stroke colour. Any CSS colour value or `var(...)` token.
   * Defaults to `var(--primary)` for brand presence; pass a token like
   * `var(--outline-variant)` for a quieter muted-grey variant.
   */
  color?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * EmblemAnimated — orb-only draw-on animation. Identical visual language to
 * LogoAnimated but without the "NULA" wordmark. Use it where a compact brand
 * mark needs to feel alive (assistant avatars, loading indicators, etc).
 *
 * Two SVG layers:
 *   1. Fill layer (bottom): a clipPath circle grows from r=0 → r=92 to reveal
 *      the orb silhouette.
 *   2. Stroke layer (top): a path is drawn via dasharray/dashoffset and
 *      clipped to the silhouette so the stroke only paints inward — no outer
 *      bleed. Fades out once the fill catches up so the two don't stack as a
 *      double outline.
 *
 * When `loop=false` the component runs the cycle once and freezes on the
 * filled silhouette (stroke faded). Consumers that want a static mark with
 * no animation can pair `loop={false}` with a `duration` of 0.
 */
export function EmblemAnimated({
  size = 200,
  loop = true,
  duration = 1670,
  holdDuration = 1000,
  color,
  className,
  style,
  ...props
}: EmblemAnimatedProps) {
  const fillCircleRef = useRef<SVGCircleElement | null>(null);
  const strokePathRef = useRef<SVGPathElement | null>(null);
  const strokeSvgRef = useRef<SVGSVGElement | null>(null);
  const rafRef = useRef<number | null>(null);

  // useId gives each instance its own clipPath ids — multiple emblems on the
  // same page must NOT share ids (Safari + Firefox both regress if they do).
  const reactId = useId().replace(/:/g, '');
  const fillClipId = `ea-clip-${reactId}`;
  const shapeClipId = `ea-shape-${reactId}`;

  useLayoutEffect(() => {
    const strokeEl = strokePathRef.current;
    const fillEl = fillCircleRef.current;
    const strokeSvg = strokeSvgRef.current;
    if (!strokeEl || !fillEl || !strokeSvg) return;

    const len =
      typeof strokeEl.getTotalLength === 'function' ? strokeEl.getTotalLength() : 0;
    strokeEl.style.strokeDasharray = `${len}px`;
    strokeEl.style.strokeDashoffset = `${len}px`;

    function reset() {
      if (!strokeEl || !fillEl || !strokeSvg) return;
      strokeEl.style.strokeDashoffset = `${len}px`;
      fillEl.setAttribute('r', '0');
      strokeSvg.style.opacity = '1';
    }

    function settleFilled() {
      if (!strokeEl || !fillEl || !strokeSvg) return;
      strokeEl.style.strokeDashoffset = '0px';
      fillEl.setAttribute('r', String(ORB_REVEAL_MAX_R));
      strokeSvg.style.opacity = '0';
    }

    function runCycle(cycleStart: number) {
      reset();

      function tick(now: number) {
        if (!strokeEl || !fillEl || !strokeSvg) return;
        const t = now - cycleStart;
        const raw = Math.min(t / duration, 1);

        strokeEl.style.strokeDashoffset = `${len * (1 - raw)}px`;
        fillEl.setAttribute('r', String(ORB_REVEAL_MAX_R * raw));

        if (t > duration) {
          // Fade stroke once fill catches up so we don't see a double outline
          // during the hold.
          strokeSvg.style.opacity = String(Math.max(0, 1 - (t - duration) / 150));
        }

        if (t < duration + holdDuration) {
          rafRef.current = requestAnimationFrame(tick);
        } else if (loop) {
          rafRef.current = requestAnimationFrame((ts) => runCycle(ts));
        } else {
          settleFilled();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame((ts) => runCycle(ts));

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [duration, holdDuration, loop]);

  // Only write --emblem-color when the caller actually overrides the
  // default — leaving it unset lets the CSS module's `var(...)` fallback
  // resolve to --primary without an explicit value here.
  const rootStyle = {
    '--emblem-width': `${size}px`,
    ...(color !== undefined ? { '--emblem-color': color } : {}),
    ...style,
  } as React.CSSProperties;

  return (
    <div
      role="img"
      aria-label="Nula emblem"
      className={clsx(styles.root, className)}
      style={rootStyle}
      {...props}
    >
      {/* Fill layer */}
      <svg viewBox={ORB_VIEW_BOX} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <clipPath id={fillClipId}>
            <circle
              ref={fillCircleRef}
              cx={ORB_REVEAL_CENTER.cx}
              cy={ORB_REVEAL_CENTER.cy}
              r="0"
            />
          </clipPath>
        </defs>
        <path clipPath={`url(#${fillClipId})`} className={styles.fillOrb} d={ORB_FILL} />
      </svg>

      {/* Stroke layer — clipped to the silhouette so stroke only paints inward */}
      <svg
        ref={strokeSvgRef}
        className={styles.stroke}
        viewBox={ORB_VIEW_BOX}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <clipPath id={shapeClipId}>
            <path d={ORB_FILL} />
          </clipPath>
        </defs>
        <path
          ref={strokePathRef}
          clipPath={`url(#${shapeClipId})`}
          className={styles.strokeOrb}
          d={ORB_STROKE_CIRCLE}
        />
      </svg>
    </div>
  );
}
